"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { User, onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import { UsuarioRPA } from "@/types/usuario";

interface AuthContextType {
  user: User | null;
  usuarioRPA: UsuarioRPA | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  usuarioRPA: null,
  loading: true,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [usuarioRPA, setUsuarioRPA] = useState<UsuarioRPA | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeDoc: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (unsubscribeDoc) {
        unsubscribeDoc();
        unsubscribeDoc = null;
      }

      if (firebaseUser) {
        setUser(firebaseUser);
        
        // Listen to the user's role and data in Firestore
        unsubscribeDoc = onSnapshot(doc(db, "usuarios_rpa", firebaseUser.uid), (docSnap) => {
          if (docSnap.exists()) {
            setUsuarioRPA({ uid: docSnap.id, ...docSnap.data() } as UsuarioRPA);
          } else {
            setUsuarioRPA(null);
          }
          setLoading(false);
        });
      } else {
        setUser(null);
        setUsuarioRPA(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeDoc) {
        unsubscribeDoc();
      }
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, usuarioRPA, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
